import React from "react";
import { isEmpty, toString } from "lodash";
import { Observer, observer } from "mobx-react";
import {
  Button,
  IconButton,
  Grid,
  Table,
  TableHead,
  TableCell,
  TableRow,
  Typography,
  makeStyles,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from "@material-ui/core";
import { Edit, AddOutlined as Add, ExpandMore } from "@material-ui/icons";
import { PrimaryButton } from "@components/buttons/PrimaryButton";
import { Form } from "@modules/formx-react";
import locale from "@bonliva-crm/locale";
import { TimereportDayFormData, TimereportDayFormStore } from "./TimereportDayFormStore";
import { dayRowActions } from "../day-row-actions";
import { PeriodTimeRow } from "../rows/PeriodTimeRow";
import { BasicTimeRow } from "../rows/BasicTimeRow";
import { TimereportDayInfo } from "../components/TimereportDayInfo";
import { BasicPeriodForm, BasicPeriodFormData } from "../components/BasicPeriodForm";
import { ExtendedPeriodForm } from "../components/ExtendedPeriodForm";
import { DayInfoForm, DayInfoData } from "../components/DayInfoForm";
import { DayExpenseForm } from "@bookings/BookingProfile/TimeReport/components/DayExpenseForm";
import Alert from "@material-ui/lab/Alert";
import { withoutRowIdentity } from "../copied-periods";

type Props = {
  store: TimereportDayFormStore;
  dayFormStores: TimereportDayFormStore[];
  canEdit: boolean;
  canEditExpenses: boolean;
  canEditOnlyMiles: boolean;
  consultantName: string;
  // A day held by the other side of a month-break week. Staff may write to it;
  // the consultant's app reads the day from the booking that owns it and will
  // not show what lands here, which is what the day says on its face.
  isOtherMonthBreakSide?: boolean;
};

export const TimereportDayForm = observer((props: Props) => {
  const { store, dayFormStores } = props;
  const { values } = store.form;
  const classes = useStyles({});
  const { withActionBar, canEditRows, canDeleteRows } = dayRowActions({
    canEdit: props.canEdit,
    canEditExpenses: props.canEditExpenses
  });

  const handleBasicMultipleDays = (values: BasicPeriodFormData) => {
    const { periods, weekDays } = values;

    if (weekDays?.length > 0) {
      weekDays.forEach((d) => {
        dayFormStores[d]?.setBasicFormData(
          dayFormStores[d] === store ? periods : withoutRowIdentity(periods)
        );
        dayFormStores[d]?.toggleDayOpen(true);
      });
    } else {
      store.setBasicFormData(periods);
    }

    store.toDefault();
  };

  const handleSickMultipleDays = (values) => {
    const { periods, weekDays } = values;
    if (weekDays?.length > 0) {
      weekDays.forEach((d) => {
        dayFormStores[d]?.setSickFormData(
          dayFormStores[d] === store ? periods : withoutRowIdentity(periods)
        );
        dayFormStores[d]?.toggleDayOpen(true);
      });
    } else {
      store.setSickFormData(periods);
    }
    store.toDefault();
  };

  const handleVabMultipleDays = (values) => {
    const { periods, weekDays } = values;
    if (weekDays?.length > 0) {
      weekDays.forEach((d) => {
        dayFormStores[d]?.setVabFormData(
          dayFormStores[d] === store ? periods : withoutRowIdentity(periods)
        );
        dayFormStores[d]?.toggleDayOpen(true);
      });
    } else {
      store.setVabFormData(periods);
    }
    store.toDefault();
  };

  const handleDayInfoMultipleDays = (values) => {
    const { weekDays, ...rest } = values;
    if (weekDays?.length > 0) {
      weekDays.forEach((d) => {
        dayFormStores[d]?.setGeneralInfo(rest);
        dayFormStores[d]?.toggleDayOpen(true);
      });
    } else {
      store.setGeneralInfo(rest);
    }
    store.toDefault();
  };

  const handleExtendedMultipleDays = (values) => {
    const { from, to, weekDays, periods } = values;
    if (weekDays?.length > 0) {
      weekDays.forEach((d) => {
        dayFormStores[d]?.setExtendedFromDataByName(
          from,
          to,
          withoutRowIdentity(periods),
          store.fsm.state
        );
        dayFormStores[d]?.toggleDayOpen(true);
      });
    } else {
      store.setExtendedFromData(from, to, periods);
    }
    store.toDefault();
  };

  const tableRows = [];

  if (!isEmpty(values.workTimeRanges)) {
    tableRows.push(
      <BasicTimeRow
        title={locale.timeReportType["Common"]}
        periods={values.workTimeRanges}
        canEdit={canEditRows}
        canDelete={canDeleteRows}
        onEdit={() => store.toEditWorkTime()}
        onDelete={() => store.deleteWorkTime()}
      />
    );
  }

  if (!isEmpty(values.sickTimeShifts)) {
    tableRows.push(
      <BasicTimeRow
        title={`${locale.timeReportType["SickDay"]}, ${
          locale[values.sickTimeShifts[0].type]
        }`}
        highlightTitle
        periods={values.sickTimeShifts}
        canEdit={canEditRows}
        canDelete={canDeleteRows}
        onEdit={() => store.toEditSickTime()}
        onDelete={() => store.deleteSickTime()}
      />
    );
  }

  if (!isEmpty(values.vabTimeShifts)) {
    tableRows.push(
      <BasicTimeRow
        title={`${locale.timeReportType["VabDay"]}, ${
          locale[values.vabTimeShifts[0].type]
        }`}
        highlightTitle
        periods={values.vabTimeShifts}
        canEdit={canEditRows}
        canDelete={canDeleteRows}
        onEdit={() => store.toEditVabTime()}
        onDelete={() => store.deleteVabTime()}
      />
    );
  }

  // The shift carries its own provenance, not its nested periods — see
  // PeriodTimeRow.
  if (!isEmpty(values.onCallShifts)) {
    values.onCallShifts.forEach((shift, i) => {
      tableRows.push(
        <PeriodTimeRow
          title={locale.timeReportType["OnCall"]}
          periodTitle={locale.timeReportType["PassiveOnCall"]}
          from={shift.period.from}
          to={shift.period.to}
          periods={shift.passivePeriods}
          source={shift.source}
          sourceId={shift.sourceId}
          sourceUrl={shift.sourceUrl}
          canEdit={canEditRows}
          canDelete={canDeleteRows}
          onEdit={() => store.toEditOnCall(i)}
          onDelete={() => store.deleteOnCall(i)}
        />
      );
    });
  }

  if (!isEmpty(values.preparednessAShifts)) {
    values.preparednessAShifts.forEach((shift, i) => {
      tableRows.push(
        <PeriodTimeRow
          title={locale.timeReportType["PreparednessA"]}
          periodTitle={locale.timeReportType["ActivePreparednessAPeriod"]}
          from={shift.period.from}
          to={shift.period.to}
          periods={shift.activePeriods}
          source={shift.source}
          sourceId={shift.sourceId}
          sourceUrl={shift.sourceUrl}
          canEdit={canEditRows}
          canDelete={canDeleteRows}
          onEdit={() => store.toEditPreparednessA(i)}
          onDelete={() => store.deletePrepA(i)}
        />
      );
    });
  }

  if (!isEmpty(values.preparednessBShifts)) {
    values.preparednessBShifts.forEach((shift, i) => {
      tableRows.push(
        <PeriodTimeRow
          title={locale.timeReportType["PreparednessB"]}
          periodTitle={locale.timeReportType["ActivePreparednessBPeriod"]}
          from={shift.period.from}
          to={shift.period.to}
          periods={shift.activePeriods}
          canEdit={canEditRows}
          canDelete={canDeleteRows}
          source={shift.source}
          sourceId={shift.sourceId}
          sourceUrl={shift.sourceUrl}
          onEdit={() => store.toEditPreparednessB(i)}
          onDelete={() => store.deletePrepB(i)}
        />
      );
    });
  }

  const editBtn = props.canEdit ? (
    <IconButton size="small" onClick={() => store.toEditGeneralInfo()}>
      <Edit />
    </IconButton>
  ) : null;

  return (
    <Form store={props.store.form} allowUpdates>
      <Accordion
        // defaultExpanded={!!isDefaultOpen}
        style={{ margin: 8 }}
        expanded={store.open}
        onChange={(e, v) => store.toggleDayOpen(v)}
        data-test="TimereportDayForm"
      >
        <AccordionSummary className={classes.summaryWrap} expandIcon={<ExpandMore />}>
          <Typography
            variant="subtitle1"
            display="inline"
            style={{ marginLeft: 16, fontSize: 15 }}
          >
            {props.store.dateStr}
          </Typography>
        </AccordionSummary>
        <AccordionDetails className={classes.detailsWrap}>
          <div className={classes.contentWrap}>
            {props.isOtherMonthBreakSide && (
              <Alert severity="warning" className={classes.monthBreakNotice}>
                {locale.MonthBreakDayOwnedByOtherBooking}
              </Alert>
            )}
            <Grid container item spacing={1}>
              <Grid item xs={5}>
                {tableRows.length > 0 && (
                  <Table size="small" className={classes.timeTable}>
                    <TableHead>
                      <TableRow>
                        <TableCell>{locale.Type}</TableCell>
                        <TableCell>{locale.From}</TableCell>
                        <TableCell>{locale.To}</TableCell>
                        <TableCell />
                      </TableRow>
                    </TableHead>

                    {tableRows}
                  </Table>
                )}
              </Grid>
              <Grid item xs={7}>
                <TimereportDayInfo
                  expenses={
                    !isEmpty(values.expenses)
                      ? values.expenses.map((x) => ({
                          amount: x.amount ?? 0,
                          comment: x.comment ?? "",
                          attachments: x.attachments
                        }))
                      : [{ amount: 0, comment: "", attachments: [] }]
                  }
                  lunch={values.lunch ?? 0}
                  mileage={toString(values.mileage).replace(".", ",")}
                  comment={values.comment}
                  editBtn={editBtn}
                  hideActions={!props.canEdit}
                  editExpense={store.toEditExpenses}
                  remExpense={store.remExpenses}
                  consultantName={props.consultantName}
                  date={store.isoDate}
                />
              </Grid>
            </Grid>
            <Observer>
              {() => (
                <>
                  {withActionBar && (
                    <div className={classes.actionsWrap}>
                      {props.canEdit && (
                        <Button
                          size={"small"}
                          disabled={store.hasMaxAllowedShifts("workTimeRanges")}
                          onClick={() => store.toEditWorkTime()}
                        >
                          <Add fontSize={"small"} />
                          {locale.AddWorkHours}
                        </Button>
                      )}

                      {props.canEdit && (
                        <Button
                          size={"small"}
                          disabled={store.hasMaxAllowedShifts("sickTimeShifts")}
                          onClick={() => store.toEditSickTime()}
                        >
                          <Add fontSize={"small"} />
                          {locale.AddSickHours}
                        </Button>
                      )}

                      {props.canEdit && (
                        <Button
                          size={"small"}
                          disabled={store.hasMaxAllowedShifts("vabTimeShifts")}
                          onClick={() => store.toEditVabTime()}
                        >
                          <Add fontSize={"small"} />
                          {locale.AddVABHours}
                        </Button>
                      )}

                      {props.canEdit && (
                        <Button onClick={() => store.toAddOnCall()} size={"small"}>
                          <Add />
                          {locale.AddOnCallHours}
                        </Button>
                      )}

                      {props.canEdit && (
                        <Button onClick={() => store.toAddPreparednessA()} size={"small"}>
                          <Add fontSize={"small"} />
                          {locale.AddOnCallAHours}
                        </Button>
                      )}

                      {props.canEdit && (
                        <Button onClick={() => store.toAddPreparednessB()} size={"small"}>
                          <Add fontSize={"small"} />
                          {locale.AddOnCallBHours}
                        </Button>
                      )}

                      {!store.isHaveDayInfo && props.canEdit && (
                        <Button onClick={() => store.toEditGeneralInfo()} size={"small"}>
                          <Add fontSize={"small"} />
                          {locale.LunchAndOther}
                        </Button>
                      )}

                      {props.canEditExpenses && (
                        <Button onClick={() => store.toEditExpenses()} size={"small"}>
                          <Add fontSize={"small"} />
                          {locale.Expense}
                        </Button>
                      )}

                      {props.canEditOnlyMiles && (
                        <Button onClick={() => store.toEditMilesInfo()} size={"small"}>
                          <Add fontSize={"small"} />
                          {locale.Milage2}
                        </Button>
                      )}

                      {/*<Button*/}
                      {/*  className={"uppercase"}*/}
                      {/*  style={{ margin: "0 10px 0 auto" }}*/}
                      {/*  onClick={() => store.form.reset(store.form.initialValues)}*/}
                      {/*  size={"small"}*/}
                      {/*>*/}
                      {/*  {locale.Reset}*/}
                      {/*</Button>*/}

                      <PrimaryButton
                        className={"uppercase"}
                        style={{ marginLeft: "auto" }}
                        color="primary"
                        size={"small"}
                        disabled={!store.form.canSubmit}
                        onClick={store.form.submit}
                      >
                        {locale.Save}
                      </PrimaryButton>
                    </div>
                  )}
                </>
              )}
            </Observer>
            {store.fsm.is("editGeneralInfo") && (
              <DayInfoForm
                open
                initialValues={getDayInfoData(store.form.values)}
                onClose={() => store.toDefault()}
                onSubmit={handleDayInfoMultipleDays}
                isLunchTimeDisabled={store.isLunchTimeDisabled()}
                lunchTimeMax={store.lunchTimeMax()}
                lunchTimeWarning={
                  store.isLunchTimeDisabled()
                    ? `Lägg till arbetad tid (07:00 - 19:00)`
                    : ""
                }
                lunchTimeHelperText={
                  !store.isLunchTimeDisabled()
                    ? `Maximal rasttid: ${store.lunchTimeMax()} minuter`
                    : undefined
                }
              />
            )}
            {store.fsm.is("editOnlyMilesInfo") && (
              <DayInfoForm
                open
                onlyMiles
                initialValues={getDayInfoData(store.form.values)}
                onClose={() => store.toDefault()}
                onSubmit={handleDayInfoMultipleDays}
              />
            )}
            {store.fsm.is("editExpenses") && (
              <DayExpenseForm
                open
                initialValues={store.expensesToEdit}
                onClose={() => store.toDefault()}
                onSubmit={(values) => store.setExpenses(values)}
              />
            )}
            {store.isBasicPeriodFormOpen && (
              <BasicPeriodForm
                maxDayShifts={store.maxDayShifts}
                initialValues={store.basicFormPeriodData.values}
                title={store.basicFormPeriodData.title}
                open
                onClose={() => store.toDefault()}
                onSubmit={handleBasicMultipleDays}
              />
            )}
            {store.isSickPeriodFormOpen && (
              <BasicPeriodForm
                isSickOrVab
                maxDayShifts={store.maxDayShifts}
                initialValues={store.sickFormPeriodData.values}
                title={store.sickFormPeriodData.title}
                open
                onClose={() => store.toDefault()}
                onSubmit={handleSickMultipleDays}
              />
            )}
            {store.isVabPeriodFormOpen && (
              <BasicPeriodForm
                isSickOrVab
                maxDayShifts={store.maxDayShifts}
                initialValues={store.vabFormPeriodData.values}
                title={store.vabFormPeriodData.title}
                open
                onClose={() => store.toDefault()}
                onSubmit={handleVabMultipleDays}
              />
            )}
            {store.isExtendedPeriodFormOpen && (
              <ExtendedPeriodForm
                initialValues={store.extendedPeriodFormData.values}
                title={store.extendedPeriodFormData.title}
                open
                onClose={() => store.toDefault()}
                onSubmit={handleExtendedMultipleDays}
                showWeekDaysInput={!store.hideWeekDaysInput}
                periodsTitle={store.extendedPeriodFormData.periodsTitle}
              />
            )}
          </div>
        </AccordionDetails>
      </Accordion>
    </Form>
  );
});

const useStyles = makeStyles((theme) => ({
  title: {
    display: "flex",
    alignItems: "center"
  },

  topWrap: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between"
  },

  monthBreakNotice: {
    marginBottom: theme.spacing(1)
  },

  // The day-info table beside this one drops the top padding of its header
  // cells; without the same rule the two columns sit half a row apart and the
  // day reads as two lines instead of one.
  timeTable: {
    "& thead th": {
      paddingTop: 0
    }
  },

  contentWrap: {
    width: "100%"
  },
  summaryWrap: {
    "&&": {
      minHeight: "auto"
    },
    padding: 0,
    "&& > div": {
      margin: "0"
    }
  },
  detailsWrap: {
    padding: "0 5px 5px"
  },

  actionsWrap: {
    marginTop: 5,
    display: "flex",
    width: "100%",
    alignItems: "center",
    "& button:not(.uppercase)": {
      fontSize: "12px",
      textTransform: "initial",
      lineHeight: 1
    },
    "& svg": {
      fontSize: "16px"
    }
  },

  dayTableWrapper: {
    borderLeft: `1px solid ${theme.palette.divider}`
  }
}));

function getDayInfoData(values: TimereportDayFormData): DayInfoData {
  return {
    lunch: toString(values.lunch ?? 0),
    miles: toString(values.mileage ?? 0),
    comment: toString(values.comment)
  };
}
